// Stable data-testid values for Playwright. This is the admin's single source; values that the gate
// journeys touch are the DOM contract in e2e/support/testids.ts (P4-D) and must stay equal to it
// (marked "e2e"). Changing a value here is a breaking change for e2e: keep them stable.
export const TID = {
  // Shell
  shell: "app-shell", // e2e shell.root
  mockBanner: "mock-banner", // visible only when the dev MSW layer is active
  skipLink: "skip-link",
  sidebar: "sidebar", // ≥ 1024 px full sidebar, 768–1023 px rail
  navGroup: (id: string) => `nav-group-${id}`, // e.g. nav-group-orders
  /** e2e shell.nav: `nav-<key>`, key = the item's path with "/" → "-" (shell/nav.ts navKey). */
  navItem: (key: string) => `nav-${key}`,
  topBar: "top-bar",
  storeSwitcher: "shell-org-switcher", // e2e shell.orgSwitcher: shows the business and the store
  storeName: "shell-store-name", // e2e shell.storeName
  storeSwitcherDialog: "store-switcher-dialog",
  storeOption: (orgSlug: string, storeSlug: string) => `store-option-${orgSlug}-${storeSlug}`,
  searchButton: "search-button", // opens the command palette (also Ctrl/Cmd+K and "/")
  commandPalette: "command-palette",
  commandPaletteInput: "command-palette-input",
  paletteItem: (id: string) => `palette-${id}`,
  sellButton: "sell-button",
  notificationsButton: "notifications-button",
  notificationsPanel: "notifications-panel",
  helpButton: "help-button",
  assistantButton: "assistant-button",
  accountMenuButton: "shell-user-menu", // e2e shell.userMenu
  accountMenu: "account-menu",
  signOut: "auth-sign-out", // e2e auth.signOut
  accountLayout: "account-layout", // account pages for a user with no business yet
  bottomBar: "bottom-bar", // < 768 px only
  bottomItem: (id: string) => `bottom-item-${id}`,
  bottomMore: "bottom-more",
  moreSheet: "more-sheet",
  moreItem: (id: string) => `more-item-${id}`, // nav entries inside the More sheet
  // Common (e2e common.*)
  toast: "toast", // rendered by @ady/ui ToastProvider
  errorState: "error-state",
  emptyState: "empty-state",
  loadingState: "loading-state",
  confirmDialogConfirm: "confirm-dialog-confirm",
  // Pages
  todayPage: "page-today",
  todayNeedsYou: "today-needs-you",
  todaySetup: "today-setup",
  placeholderPage: "page-placeholder",
  securityPage: "page-security",
  sessionsPage: "page-sessions",
  // Sessions (e2e sessions.*)
  sessionsTable: "sessions-table", // the session list region
  sessionRow: "session-row",
  sessionCurrent: "session-current",
  revokeSession: (id: string) => `revoke-session-${id}`,
  revokeAllSessions: "sessions-signout-others",
  // MFA (e2e mfa.*)
  totpStart: "mfa-totp-enroll",
  totpSecret: "mfa-totp-secret",
  totpCode: "mfa-totp-code-input",
  totpConfirm: "mfa-totp-confirm",
  totpStatus: "mfa-totp-status", // data-state="enabled" | "off"
  recoveryCodes: "mfa-recovery-codes",
  recoveryCode: "mfa-recovery-code",
  passkeyAdd: "mfa-passkey-add",
  passkeyItem: "mfa-passkey-item",
  // Step-up (e2e stepUp.*)
  stepUpDialog: "stepup-dialog",
  stepUpMethod: (method: "otp" | "sms" | "totp" | "passkey" | "recovery_code") =>
    `stepup-method-${method.replace("_", "-")}`,
  stepUpCodeInput: "stepup-code-input",
  stepUpConfirm: "stepup-confirm",
  // Onboarding (e2e onboarding.*)
  onboardingPage: "page-onboarding",
  onboardingOrgName: "onboarding-org-name-input",
  onboardingStoreSlug: "onboarding-store-slug-input",
  onboardingSubmit: "onboarding-org-submit",
  legalGate: "page-legal",
  legalAccept: "legal-accept",
  // Auth (e2e auth.*)
  signInPage: "page-sign-in",
  emailInput: "auth-email-input",
  sendCode: "auth-otp-start",
  googleButton: "auth-google-button",
  passkeyButton: "auth-passkey-button",
  verifyPage: "page-verify",
  codeInput: "auth-otp-code-input",
  verifyButton: "auth-otp-verify",
  otpError: "auth-otp-error",
  resendButton: "resend-button",
  mfaPage: "page-mfa",
  mfaCode: "mfa-code",
  mfaVerify: "mfa-verify",
  formError: "form-error",
  // Team (TEAM-001; e2e team.*)
  teamPage: "page-team",
  memberRow: "team-member-row",
  memberRoleSelect: "member-role-select",
  memberRoleSave: "member-role-save",
  memberRemove: "member-remove",
  inviteButton: "team-invite-button",
  inviteDialog: "invite-dialog",
  inviteEmailInput: "invite-email-input",
  inviteRoleSelect: "invite-role-select",
  inviteSubmit: "invite-submit",
  /** The manual-delivery link shown once after an invitation is created (not yet in e2e). */
  inviteLink: "invite-link",
  inviteLinkCopy: "invite-link-copy",
  invitationRow: "team-invitation-row",
  invitationRevoke: "invitation-revoke",
  invitationPage: "page-invitation",
  invitationOffer: "invitation-offer",
  invitationAccept: "invitation-accept-button",
  // Entitlements (BILLING-09; e2e entitlements.*)
  quotaBanner: "quota-exceeded-banner",
  usageMeter: "usage-meter", // one per quota, with data-quota="<key>"
  billingPage: "page-billing",
  // Audit (AUDIT-001; e2e audit.row with data-action)
  activityPage: "page-activity",
  auditRow: "audit-row",
  auditLoadMore: "audit-load-more",
} as const;
